import { API } from "../../services/api";
import { registerSessionCleanup } from "../../auth/authStorage";

// Les brouillons contiennent des données patient : ils ne sont plus écrits dans le
// navigateur (SEC-008). Ils restent en mémoire pendant l'édition et sont envoyés à
// l'API serveur, isolée par utilisateur et par pharmacie, pour survivre au rechargement.
export const LEGACY_DRAFT_PREFIX = "commande-draft:";
const BACKUP_VERSION = 2;
const MAX_LEGACY_BACKUP_AGE_MS = 2 * 60 * 60 * 1000;
export const DRAFT_SYNC_DELAY_MS = 1000;
const REQUEST_OPTIONS = { noRedirectOn401: true };

const backups = new Map();
const syncTimers = new Map();
// Change à chaque purge : une réponse serveur arrivée après la déconnexion est ignorée.
let generation = 0;

function urlFor(commandeId) {
    return `/commandes/${commandeId}/draft`;
}

function legacyKeyFor(commandeId) {
    return `${LEGACY_DRAFT_PREFIX}${commandeId}`;
}

function removeLegacy(key) {
    try {
        localStorage.removeItem(key);
    } catch {
        // localStorage peut être indisponible.
    }
}

function cancelSync(commandeId) {
    const key = String(commandeId);
    clearTimeout(syncTimers.get(key));
    syncTimers.delete(key);
}

function pushToServer(commandeId, draft) {
    return Promise.resolve(API.put(urlFor(commandeId), { draft }, REQUEST_OPTIONS));
}

function flush(commandeId) {
    syncTimers.delete(String(commandeId));
    const backup = backups.get(String(commandeId));
    if (!backup) return;
    pushToServer(commandeId, backup.draft).catch(() => {
        // Le brouillon reste en mémoire : le prochain changement retentera l'envoi.
    });
}

export function saveDraftBackup(commandeId, draft) {
    if (!commandeId || !draft) return;
    const key = String(commandeId);
    backups.set(key, { version: BACKUP_VERSION, draft, savedAt: new Date().toISOString() });
    cancelSync(key);
    syncTimers.set(key, setTimeout(() => flush(key), DRAFT_SYNC_DELAY_MS));
}

/** Brouillon connu en mémoire pour la commande, sans appel réseau. */
export function peekDraftBackup(commandeId) {
    return backups.get(String(commandeId)) ?? null;
}

/** Ancien brouillon local encore exploitable (format courant, récent), sinon null ; les autres sont supprimés. */
function readLegacyBackup(key) {
    let raw;
    try {
        raw = localStorage.getItem(key);
    } catch {
        return null;
    }
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        const savedAt = Date.parse(parsed?.savedAt);
        const expired = !Number.isFinite(savedAt) || Date.now() - savedAt > MAX_LEGACY_BACKUP_AGE_MS;
        if (parsed?.version === BACKUP_VERSION && !expired && parsed?.draft) return parsed;
    } catch {
        // JSON corrompu : supprimé ci-dessous.
    }
    removeLegacy(key);
    return null;
}

/** Envoie l'ancien brouillon local au serveur et ne retire la clé locale qu'une fois l'envoi confirmé. */
function migrateLegacy(commandeId, backup) {
    return pushToServer(commandeId, backup.draft)
        .then(() => removeLegacy(legacyKeyFor(commandeId)))
        .catch(() => {
            // Migration non destructive : la copie locale reste jusqu'à un envoi réussi.
        });
}

export async function loadDraftBackup(commandeId) {
    if (!commandeId) return null;
    const key = String(commandeId);
    const inMemory = backups.get(key);
    if (inMemory) return inMemory;

    const legacy = readLegacyBackup(legacyKeyFor(key));
    if (legacy) {
        backups.set(key, legacy);
        await migrateLegacy(key, legacy);
        return legacy;
    }

    const requestGeneration = generation;
    try {
        const { data } = await API.get(urlFor(key), REQUEST_OPTIONS);
        if (requestGeneration !== generation || !data?.draft) return null;
        const backup = { version: BACKUP_VERSION, draft: data.draft, savedAt: data.savedAt ?? null };
        backups.set(key, backup);
        return backup;
    } catch {
        return null;
    }
}

/** Reprend tous les anciens brouillons encore valides du poste vers le serveur. */
export async function migrateLegacyDraftBackups() {
    let keys = [];
    try {
        keys = Object.keys(localStorage).filter(key => key.startsWith(LEGACY_DRAFT_PREFIX));
    } catch {
        return;
    }
    await Promise.all(keys.map(key => {
        const backup = readLegacyBackup(key);
        if (!backup) return null;
        const commandeId = key.slice(LEGACY_DRAFT_PREFIX.length);
        // Gardé en mémoire : une commande ouverte pendant la migration retrouve son brouillon.
        if (!backups.has(commandeId)) backups.set(commandeId, backup);
        return migrateLegacy(commandeId, backup);
    }));
}

export function clearDraftBackup(commandeId) {
    if (!commandeId) return;
    const key = String(commandeId);
    cancelSync(key);
    backups.delete(key);
    removeLegacy(legacyKeyFor(key));
    Promise.resolve(API.delete(urlFor(key), REQUEST_OPTIONS)).catch(() => {});
}

/** Oublie tous les brouillons du poste (déconnexion, expiration, changement d'utilisateur). */
export function clearAllDraftBackups() {
    generation += 1;
    syncTimers.forEach(timer => clearTimeout(timer));
    syncTimers.clear();
    backups.clear();
    try {
        Object.keys(localStorage)
            .filter(key => key.startsWith(LEGACY_DRAFT_PREFIX))
            .forEach(removeLegacy);
    } catch {
        // localStorage peut être indisponible.
    }
}

registerSessionCleanup(clearAllDraftBackups);
